import { useCallback, useMemo } from 'react'
import { targetEnv } from '@fs/zion-config'
import { useMemoryBook } from '../../providers/MemoryBookProvider'

// Configuration constants
const COLOR_VALUES_MAP = {
  purple: {
    iconColor: 'rgb(140, 63, 204)', // purple60
    backgroundBoxColor: 'rgb(243, 233, 249)', // purple03
    surnameCircleColor: 'rgba(208, 168, 243, 0.7)', // purple20
  },
  yellow: {
    iconColor: 'rgb(177, 68, 0)', // yellow60
    backgroundBoxColor: 'rgb(249, 236, 179)', // yellow03
    surnameCircleColor: 'rgba(243, 170, 12, 0.7)', // yellow20
  },
  blue: {
    iconColor: 'rgb(5, 110, 144)', // blue60
    backgroundBoxColor: 'rgb(224, 237, 246)', // blue03
    surnameCircleColor: 'rgba(91, 197, 222, 0.7)', // blue20
  },
}

const IMAGE_CDN_BASE_URL_MAP = {
  local: 'https://chinese-homeland-igd.familysearchcdn.org',
  int: 'https://chinese-homeland-igd.familysearchcdn.org',
  beta: 'https://chinese-homeland-bgt.familysearchcdn.org',
  prod: 'https://chinese-homeland-pgp.familysearchcdn.org',
}

const COLOR_OVERRIDES_MAP = {
  10: 'yellow',
  403: 'purple',
  229: 'blue',
  470: 'blue',
}

const ID_HAS_NO_ORIGIN_LIST = [344, 461, 488]

const useSurnameMapsAndColors = () => {
  const { memoryBook } = useMemoryBook()
  const contentPages = useMemo(() => memoryBook?.contentPages || [], [memoryBook?.contentPages])

  // Helper function to check if an ID has no origin
  const idHasNoOrigin = (id) => ID_HAS_NO_ORIGIN_LIST.includes(id)

  const fetchSurnameMaps = useCallback(async (id, color) => {
    const baseUrl = IMAGE_CDN_BASE_URL_MAP[targetEnv]
    return {
      distributionMap: `${baseUrl}/maps/distribution/${color}/${id}.webp`,
      originMap: idHasNoOrigin(id)
        ? `${baseUrl}/maps/origins/no-origins.webp`
        : `${baseUrl}/maps/origins/${color}/${id}.webp`,
    }
  }, [])

  // Count the occurrences of each color in the content pages to determine next color
  const countColors = useCallback(() => {
    const colorCounts = { purple: 0, blue: 0, yellow: 0 }

    contentPages.forEach((page) => {
      if (page.templateId === 2 && page.elements.length > 0) {
        const color = page?.elements[0]?.props?.color
        if (color in colorCounts) {
          colorCounts[color]++
        }
      }
    })

    return colorCounts
  }, [contentPages])

  const fetchNextSurnameColor = useCallback(
    (surnameId) => {
      if (surnameId in COLOR_OVERRIDES_MAP) {
        return COLOR_OVERRIDES_MAP[surnameId]
      }

      const colorCounts = countColors()
      const themeColors = ['purple', 'yellow', 'blue']

      // Find the color with the least count
      return themeColors.reduce(
        (leastColor, color) => (colorCounts[color] < colorCounts[leastColor] ? color : leastColor),
        themeColors[0]
      )
    },
    [countColors]
  )

  return {
    fetchSurnameMaps,
    fetchNextSurnameColor,
    colorValuesMap: COLOR_VALUES_MAP,
  }
}

export default useSurnameMapsAndColors
